import { Box, Checkbox, Grid2, Popover } from "@mui/material";
import { ActionIconsV2, DataGrid, Modal, Tabs, Toast } from "../common";
import {
  ApiBasePathParams,
  BulkEditWashInventoryFormData,
  NoteItem,
  NotesInfo,
  NotesRequestParams,
  TabItem,
  ToastOptions,
  UserPermission,
  WashesInfo,
  WashesRequestParams,
  WashInventoryFilters,
  WashInventoryFormData,
  WashItem,
} from "../../types";
import { downloadFile, theme } from "../../utils";
import { useAtom } from "jotai";
import {
  selectedAccountAtom,
  selectedOrgIdAtom,
  userAtom,
  userPermissionAtom,
  washFilterStateAtom,
  washInventoryDataGridPaginationAtom,
} from "../../atoms";
import {
  GridColDef,
  GridRenderCellParams,
  GridSortDirection,
  GridSortModel,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
import { formatDate, getDateRange, isValidDate } from "../../utils/dateUtil";
import { washInventoryFiltersAtom } from "../../atoms/washInventoryAtoms";
import {
  useBulkUpdateWashInventoryMutation,
  useCreateWashInventoryMutation,
  useDeleteWashInventoriesMutation,
  useDeleteWashInventoryMutation,
  useDeleteWashNoteMutation,
  useFetchDownloadWashesCsvQuery,
  useFetchPaginatedWashesQuery,
  useFetchPaginatedWashNotesQuery,
  useUpdateWashInventoryMutation,
  useUpdateWashNoteMutation,
} from "../../api/washInventoryService";
import { Action, DateRangeOptions, Module, Role } from "../../enums";
import WashInventoryToolBar from "./fragments/washInventoryToolBar";
import {
  ManageWashInventoryForm,
  BulkEditWashInventoryForm,
  EditNotesForm,
} from "./fragments";
import { useHasPermission } from "@zdistancelab-packages/ui-permission-validator";
import { queryClient } from "../../App";
import { v4 as uuidv4 } from "uuid";
import { useSearchParams } from "react-router-dom";
import React from "react";

export const WashInventory = () => {
  const [user] = useAtom(userAtom);
  const [userPermission] = useAtom<UserPermission>(userPermissionAtom);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [selectedAccount] = useAtom(selectedAccountAtom);
  const [filterValues, setFilterValues] = useAtom(washFilterStateAtom);
  const [washInventoryList, setWashInventoryList] = useState<WashesInfo>();
  const [notesList, setNotesList] = useState<NotesInfo>();
  const [queryParams, setQueryParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [washInventoryPaginationModel, setWashInventoryPaginationModel] =
    useAtom(washInventoryDataGridPaginationAtom);
  const [selectedWashInventories, setSelectedWashInventories] = useState<
    number[]
  >([]);
  const [washInventoryFilters, setWashInventoryFilters] = useAtom(
    washInventoryFiltersAtom,
  );
  const hasWashInventoryWritePermission = useHasPermission(userPermission, [
    { module: Module.WashInventory, action: Action.Write },
  ]);
  const hasWashInventoryDeletePermission = useHasPermission(userPermission, [
    { module: Module.WashInventory, action: Action.Delete },
  ]);
  const hasNotesWritePermission = useHasPermission(userPermission, [
    { module: Module.WashNote, action: Action.Write },
  ]);
  const hasNotesDeletePermission = useHasPermission(userPermission, [
    { module: Module.WashNote, action: Action.Delete },
  ]);
  const [toastOptions, setToastOptions] = useState<ToastOptions>({
    open: false,
    message: "",
    severity: "info",
  });
  const [selectedDateRange, setSelectedDateRange] =
    useState<string>("thisMonth");
  const updateWashInventoryMutation = useUpdateWashInventoryMutation();
  const updateWashNoteMutation = useUpdateWashNoteMutation();
  const deleteWashNoteMutation = useDeleteWashNoteMutation();
  const createWashInventoryMutation = useCreateWashInventoryMutation();
  const bulkUpdateWashInventoryMutation = useBulkUpdateWashInventoryMutation();
  const deleteWashInventoryMutation = useDeleteWashInventoryMutation();
  const deleteWashInventoriesMutation = useDeleteWashInventoriesMutation();
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const handleOpenBulkEditModal = () => setIsBulkEditModalOpen(true);
  const handleCloseBulkEditModal = () => setIsBulkEditModalOpen(false);
  const [isAddWashModalOpen, setIsAddWashModalOpen] = useState(false);
  const handleCloseWashModal = () => setIsAddWashModalOpen(false);
  const handleCreateWash = (data: WashInventoryFormData) => {
    const vehicles =
      data.unitOfMeasurement === "hours"
        ? [
            {
              vehicleNumber: "N/A",
              comments: "Admin app",
            },
          ]
        : data.vehicleNumbers && data.vehicleNumbers.length > 0
          ? data.vehicleNumbers.map((vehicleNumber) => ({
              vehicleNumber: vehicleNumber || "N/A",
              comments: "Admin app",
            }))
          : [
              {
                vehicleNumber: "N/A",
                comments: "Admin app",
              },
            ];
    const uuid =
      data.unitOfMeasurement === "hours"
        ? [uuidv4()]
        : data.vehicleNumbers?.map(() => uuidv4());

    createWashInventoryMutation.mutate({
      orgId: selectedOrgId,
      teamId: 3,
      uuid: uuid || [],
      comments: "N/A",
      startTime: data.endTime || "",
      quantity: Number(data.hours) || 1,
      vehicles,
      accountId: data.accountId,
      washTypeId: data.washTypeId,
      leadId: data.leadId,
      endTime: data.endTime || "",
      locationNumber: data.locationNumber,
    });
    handleCloseWashModal();
  };

  const tabItems: TabItem[] = [
    {
      key: "Washes",
      value: 0,
    },
    {
      key: "Notes",
      value: 1,
    },
  ];

  const handleTabChange = (index: number) => {
    resetPage();
    setSelectedTab(index);
    setWashInventoryFilters({
      ...washInventoryFilters,
      search: "",
    });
  };

  const isWashTabSelected = (): boolean => {
    return selectedTab === 0;
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field;
      const sortOrder: GridSortDirection = sortModel[0].sort;
      if (sortOrder) {
        setWashInventoryFilters({
          ...washInventoryFilters,
          sortBy: sortField,
          sortOrder: sortOrder,
        });
      }
    } else if (washInventoryFilters.sortBy !== "") {
      setWashInventoryFilters({
        ...washInventoryFilters,
        sortBy: "",
        sortOrder: "",
      });
    }
  };
  const buildRequestParams = (): WashesRequestParams | NotesRequestParams => {
    return {
      orgId: selectedOrgId,
      accountId:
        user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin
          ? washInventoryFilters?.accountId || 0
          : selectedAccount?.accountId || 0,
      filters: {
        page: washInventoryFilters?.page ? washInventoryFilters?.page + 1 : 1,
        pageSize: washInventoryFilters?.pageSize,
        fromDate: washInventoryFilters?.fromDate,
        toDate: washInventoryFilters?.toDate,
        sortBy: washInventoryFilters?.sortBy,
        sortOrder: washInventoryFilters?.sortOrder,
        washTypeId: isWashTabSelected()
          ? washInventoryFilters?.washTypeId
          : undefined,
        userId: washInventoryFilters?.userId,
        vehicleNumber: isWashTabSelected()
          ? washInventoryFilters?.vehicleNumber
          : undefined,
        locationNumber: washInventoryFilters?.locationNumber,
        search: !isWashTabSelected() ? washInventoryFilters?.search : undefined,
      },
    };
  };

  const requestParams = buildRequestParams();

  const isAllowedToFetch =
    selectedOrgId &&
    !(user?.roleCode === Role.Customer && requestParams.accountId === 0);

  const fetchWashInventoryQuery = useFetchPaginatedWashesQuery(
    requestParams,
    isAllowedToFetch && isWashTabSelected() ? true : false,
  );

  const fetchWashNotesQuery = useFetchPaginatedWashNotesQuery(
    requestParams,
    isAllowedToFetch && !isWashTabSelected() ? true : false,
  );

  const downloadWashInventoryQuery = useFetchDownloadWashesCsvQuery(
    requestParams,
    { enabled: false },
  );
  const handleEditWashInventory = (data: WashInventoryFormData) => {
    const washItem: WashItem | undefined = washInventoryList?.items.find(
      (item) => item.id === data.id,
    );

    if (washItem) {
      updateWashInventoryMutation.mutate({
        ...washItem,
        orgId: washItem.orgId,
        teamId: user?.teamId || 0,
        comments: "Manually updated from Sprayzapp.",
        endTime:
          formatDate(washItem.endTime, "MM/DD/YYYY") !== data.endTime
            ? formatDate(data.endTime || "", "YYYY-MM-DD HH:mm:ss")
            : washItem.endTime,
        startTime:
          formatDate(washItem.endTime, "MM/DD/YYYY") !== data.endTime
            ? formatDate(data.endTime || "", "YYYY-MM-DD HH:mm:ss")
            : washItem.endTime,
        vehicleNumber: data.vehicleNumbers?.length
          ? data.vehicleNumbers[0]
          : "N/A",
        quantity: Number(data.hours) || 1,
        leadId: data.leadId,
        washTypeId: data.washTypeId,
      });
    }
  };

  const handleEditNotes = (
    data: Pick<NoteItem, "id" | "orgId" | "accountId" | "note">,
  ) => {
    updateWashNoteMutation.mutate({
      id: data.id,
      orgId: data.orgId,
      accountId: data.accountId,
      note: data.note,
    });
  };

  const handleNoteSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
    params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
  ) => {
    updateWashNoteMutation.mutate({
      id: params.row.id,
      orgId: params.row.orgId,
      accountId: params.row.accountId,
      isSelected: event.target.checked,
    });
  };

  const handleDeleteNotes = (data: NoteItem) => {
    deleteWashNoteMutation.mutate({
      id: data.id,
      orgId: data.orgId,
      accountId: data.accountId,
    });
  };

  const handleDeleteWashInventory = (data: WashItem) => {
    deleteWashInventoryMutation.mutate({
      orgId: data.orgId,
      id: data.id,
    });
  };

  const columns = useMemo<GridColDef[]>(() => {
    const washesColumns: GridColDef[] = [
      {
        field: "endTime",
        headerName: "Date",
        width: 210,
        editable: false,
        valueGetter: (value) => {
          return formatDate(value, "dddd - MMMM DD,YYYY");
        },
      },
      {
        field: "accountName",
        headerName: "Account Name",
        minWidth: 220,
        editable: false,
      },
      {
        field: "leadName",
        headerName: "Lead",
        width: 170,
        editable: false,
      },
      {
        field: "washTypeName",
        headerName: "Wash Type",
        width: 170,
        editable: false,
      },
      {
        field: "quantity",
        headerName: "Quantity",
        width: 80,
        headerAlign: "center",
        align: "center",
        editable: false,
      },
      {
        field: "vehicleNumber",
        headerName: "Vehicle Number",
        width: 150,
        headerAlign: "center",
        align: "center",
        editable: false,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 200,
        editable: false,
        renderCell: (params) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(
            null,
          );
          const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
          };
          const handlePopoverClose = () => {
            setAnchorEl(null);
          };
          const open = Boolean(anchorEl);

          if (!params.value || params.value.length === 0) {
            return null;
          }

          if (params.value.length === 1) {
            return <span>{params.value[0]}</span>;
          }

          return (
            <>
              <span
                onClick={handlePopoverOpen}
                style={{
                  color: "#1976d2",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                  maxWidth: 170,
                }}
              >
                View Notes
              </span>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    width: 320,
                    wordBreak: "break-word",
                    fontSize: "14px",
                    p: "16px 24px",
                  },
                }}
                disableRestoreFocus
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    fontSize: "inherit",
                  }}
                >
                  Notes
                </div>
                <ul style={{ padding: 0, margin: 0, paddingLeft: 20 }}>
                  {params.value?.map((note: string, index: number) => (
                    <li
                      key={index}
                      style={{ fontSize: "inherit", marginBottom: 4 }}
                    >
                      {note}
                    </li>
                  ))}
                </ul>
              </Popover>
            </>
          );
        },
      },
    ];

    const notesColumns: GridColDef[] = [
      {
        field: "noteDate",
        headerName: "Date",
        width: 210,
        editable: false,
        valueGetter: (value) => {
          return formatDate(value, "dddd - MMMM DD,YYYY");
        },
      },
      {
        field: "accountName",
        headerName: "Account Name",
        minWidth: 220,
        editable: false,
      },
      {
        field: "leadName",
        headerName: "Lead",
        width: 170,
        editable: false,
      },
      {
        field: "locationName",
        headerName: "Location",
        width: 170,
        editable: false,
      },
      {
        field: "vehicleNumber",
        headerName: "Vehicle Number",
        width: 170,
        editable: false,
      },
      {
        field: "note",
        headerName: "Notes",
        width: 310,
        editable: false,
      },
      {
        field: "isSelected",
        headerName: "Add Note",
        width: 100,
        editable: false,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => (
          <Checkbox
            checked={params.row.isSelected}
            onChange={(e) => handleNoteSelection(e, params)}
            sx={{
              "& .MuiSvgIcon-root": {
                fontSize: 16,
              },
            }}
          />
        ),
      },
    ];

    if (hasWashInventoryWritePermission || hasWashInventoryDeletePermission) {
      washesColumns.push({
        field: "Actions",
        headerName: "Actions",
        width: 100,
        editable: false,
        sortable: false,
        renderCell: (params) => (
          <ActionIconsV2
            onEdit={handleEditWashInventory}
            onDelete={handleDeleteWashInventory}
            hasWritePermission={hasWashInventoryWritePermission}
            hasDeletePermission={hasWashInventoryDeletePermission}
            rowData={params.row}
            formProps={{
              form: ManageWashInventoryForm,
              name: "editWashInventoryForm",
            }}
            editProps={{
              title: "Edit Wash",
              primaryButtonText: "Save",
            }}
            deleteProps={{
              title: "Delete Wash",
            }}
          />
        ),
      });

      notesColumns.push({
        field: "Actions",
        headerName: "Actions",
        width: 100,
        editable: false,
        sortable: false,
        renderCell: (params) => (
          <ActionIconsV2
            onEdit={handleEditNotes}
            onDelete={handleDeleteNotes}
            hasWritePermission={hasNotesWritePermission}
            hasDeletePermission={hasNotesDeletePermission}
            rowData={params.row}
            formProps={{
              form: EditNotesForm,
              name: "editWashInventoryForm",
            }}
            editProps={{
              title: "Edit Note",
              primaryButtonText: "Save",
            }}
            deleteProps={{
              title: "Delete Note",
            }}
          />
        ),
      });
    }
    return isWashTabSelected() ? washesColumns : notesColumns;
  }, [selectedTab, washInventoryList]);

  const handleBulkUpdateButtonClick = () => {
    handleOpenBulkEditModal();
  };

  const handleAddButtonClick = () => {
    setIsAddWashModalOpen(true);
  };

  const handleRemoveButtonClick = () => {
    deleteWashInventoriesMutation.mutate({
      orgId: selectedOrgId,
      ids: selectedWashInventories,
    });
  };

  const handleBulkEdit = (data: any) => {
    const washInventoryData: ApiBasePathParams & BulkEditWashInventoryFormData =
      {
        orgId: selectedOrgId,
        accountId: 0,
        ids: selectedWashInventories,
        ...(data.washTypeId && { washTypeId: data.washTypeId }),
        ...(data.vehicleNumber && { vehicleNumber: data.vehicleNumber }),
        ...(data.leadId && { leadId: data.leadId }),
        ...(isValidDate(data.endTime) && {
          endTime: formatDate(data.endTime, "YYYY-MM-DD HH:mm:ss"),
          bulkEditDate: formatDate(data.endTime, "YYYY-MM-DD HH:mm:ss"),
        }),
      };
    bulkUpdateWashInventoryMutation.mutate(washInventoryData);
    handleCloseBulkEditModal();
  };

  const handleRowSelectionChange = (selectedRowIds: number[]) => {
    setSelectedWashInventories(selectedRowIds);
  };

  const handleRefresh = () => {
    if (isWashTabSelected()) {
      isAllowedToFetch && fetchWashInventoryQuery.refetch();
    } else {
      isAllowedToFetch && fetchWashNotesQuery.refetch();
    }
  };
  const handleDownload = () => {
    isAllowedToFetch && downloadWashInventoryQuery.refetch();
  };

  const resetPage = () => {
    setWashInventoryPaginationModel({
      ...washInventoryPaginationModel,
      page: 0,
    });
  };

  useEffect(() => {
    if (
      downloadWashInventoryQuery.isSuccess &&
      downloadWashInventoryQuery.data
    ) {
      const accountName = selectedAccount ? selectedAccount.accountName : "";
      const fileName =
        [
          user?.roleCode === Role.SuperAdmin || user?.roleCode === Role.Admin
            ? ""
            : accountName,
          washInventoryFilters.fromDate || "",
          washInventoryFilters.fromDate && washInventoryFilters.toDate
            ? "to"
            : "",
          washInventoryFilters.toDate || "",
        ]
          .filter(Boolean)
          .join("_") + "_wash-inventory.csv";
      const blob = new Blob([downloadWashInventoryQuery.data], {
        type: "text/csv",
      });
      const linkToCsv = URL.createObjectURL(blob);
      if (linkToCsv) downloadFile(linkToCsv, fileName);

      queryClient.resetQueries("downloadWashes");
    }
  }, [
    downloadWashInventoryQuery.isSuccess,
    downloadWashInventoryQuery.data,
    downloadWashInventoryQuery.dataUpdatedAt,
  ]);

  useEffect(() => {
    if (fetchWashInventoryQuery.isSuccess) {
      setWashInventoryList(fetchWashInventoryQuery.data);
    }
  }, [fetchWashInventoryQuery.isSuccess, fetchWashInventoryQuery.data]);

  useEffect(() => {
    if (fetchWashNotesQuery.isSuccess) {
      setNotesList(fetchWashNotesQuery.data);
    }
  }, [fetchWashNotesQuery.isSuccess, fetchWashNotesQuery.data]);

  useEffect(() => {
    setWashInventoryFilters({
      ...washInventoryFilters,
      page: washInventoryPaginationModel.page,
      pageSize: washInventoryPaginationModel.pageSize,
    });
  }, [washInventoryPaginationModel]);

  useEffect(() => {
    if (deleteWashInventoryMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash inventory deleted successfully.",
        severity: "success",
      });
      fetchWashInventoryQuery.refetch();
    } else if (deleteWashInventoryMutation.isError) {
      setToastOptions({
        open: true,
        message: "Wash inventory delete failed",
        severity: "error",
      });
    }
  }, [
    deleteWashInventoryMutation.isLoading,
    deleteWashInventoryMutation.isSuccess,
    deleteWashInventoryMutation.isError,
  ]);

  useEffect(() => {
    if (deleteWashInventoriesMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash inventories deleted successfully.",
        severity: "success",
      });
      fetchWashInventoryQuery.refetch();
    } else if (deleteWashInventoriesMutation.isError) {
      setToastOptions({
        open: true,
        message: "Wash inventories delete failed",
        severity: "error",
      });
    }
  }, [
    deleteWashInventoriesMutation.isLoading,
    deleteWashInventoriesMutation.isSuccess,
    deleteWashInventoriesMutation.isError,
  ]);

  useEffect(() => {
    if (updateWashInventoryMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash inventory updated",
        severity: "success",
      });
      fetchWashInventoryQuery.refetch();
    } else if (updateWashInventoryMutation.isError) {
      setToastOptions({
        open: true,
        message: "Wash inventory update failed",
        severity: "error",
      });
    }
  }, [
    updateWashInventoryMutation.isLoading,
    updateWashInventoryMutation.isSuccess,
    updateWashInventoryMutation.isError,
  ]);

  useEffect(() => {
    if (updateWashNoteMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash note updated",
        severity: "success",
      });
      fetchWashNotesQuery.refetch();
    } else if (updateWashNoteMutation.isError) {
      setToastOptions({
        open: true,
        message: "Wash note update failed",
        severity: "error",
      });
    }
  }, [
    updateWashNoteMutation.isLoading,
    updateWashNoteMutation.isSuccess,
    updateWashNoteMutation.isError,
  ]);

  useEffect(() => {
    if (deleteWashNoteMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash note deleted successfully.",
        severity: "success",
      });
      fetchWashNotesQuery.refetch();
    } else if (deleteWashNoteMutation.isError) {
      setToastOptions({
        open: true,
        message: "Failed to delete wash note",
        severity: "error",
      });
    }
  }, [
    deleteWashNoteMutation.isLoading,
    deleteWashNoteMutation.isSuccess,
    deleteWashNoteMutation.isError,
  ]);

  useEffect(() => {
    if (createWashInventoryMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash inventory created",
        severity: "success",
      });
      fetchWashInventoryQuery.refetch();
    } else if (createWashInventoryMutation.isError) {
      if (createWashInventoryMutation.error.status === 409) {
        setToastOptions({
          open: true,
          message:
            "This action will lead to duplicate wash inventory. So can not proceed.",
          severity: "error",
        });
      } else {
        setToastOptions({
          open: true,
          message: "Wash inventory create failed",
          severity: "error",
        });
      }
    }
  }, [
    createWashInventoryMutation.isLoading,
    createWashInventoryMutation.isSuccess,
    createWashInventoryMutation.isError,
  ]);

  useEffect(() => {
    if (bulkUpdateWashInventoryMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Wash inventories updated.",
        severity: "success",
      });
      fetchWashInventoryQuery.refetch();
    } else if (bulkUpdateWashInventoryMutation.isError) {
      if (bulkUpdateWashInventoryMutation.error.status === 409) {
        setToastOptions({
          open: true,
          message:
            "This action will lead to duplicate wash inventory. So can not proceed.",
          severity: "error",
        });
      } else {
        setToastOptions({
          open: true,
          message: "Failed to update wash inventory.",
          severity: "error",
        });
      }
    }
  }, [
    bulkUpdateWashInventoryMutation.isLoading,
    bulkUpdateWashInventoryMutation.isSuccess,
    bulkUpdateWashInventoryMutation.isError,
    bulkUpdateWashInventoryMutation.error,
  ]);

  useEffect(() => {
    const dateRange = queryParams.get("dateRange");
    const dateFilters: Partial<WashInventoryFilters> = {
      fromDate: queryParams.get("fromDate") || undefined,
      toDate: queryParams.get("toDate") || undefined,
    };
    if (
      dateRange &&
      Object.values(DateRangeOptions).includes(dateRange as DateRangeOptions)
    ) {
      if (dateRange !== DateRangeOptions.CustomDate) {
        dateFilters.fromDate = getDateRange(dateRange).startDate;
        dateFilters.toDate = getDateRange(dateRange).endDate;
      }
      dateFilters.dateRange = dateRange;

      setSelectedDateRange(dateRange);
    }

    const queryFilters: WashInventoryFilters = {
      orgId: queryParams.get("orgId")
        ? Number(queryParams.get("orgId"))
        : undefined,
      accountId: queryParams.get("accountId")
        ? Number(queryParams.get("accountId"))
        : undefined,
      locationNumber: queryParams.get("locationNumber")
        ? Number(queryParams.get("locationNumber"))
        : undefined,
      userId: queryParams.get("userId")
        ? Number(queryParams.get("userId"))
        : undefined,
      washTypeId: queryParams.get("washTypeId")
        ? Number(queryParams.get("washTypeId"))
        : undefined,
      vehicleNumber: queryParams.get("vehicleNumber")
        ? queryParams.get("vehicleNumber")?.toString()
        : undefined,
      page: queryParams.get("page")
        ? Number(queryParams.get("page"))
        : undefined,
      pageSize: queryParams.get("pageSize")
        ? Number(queryParams.get("pageSize"))
        : undefined,
      sortBy: queryParams.get("sortBy") || undefined,
      sortOrder: queryParams.get("sortOrder") || undefined,
    };

    const tempWashInventoryFiltersFromQueryStrings = Object.entries(
      queryFilters,
    ).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const tempFilterValuesFromQueryStrings = Object.entries({
      orgId: tempWashInventoryFiltersFromQueryStrings.orgId,
      accountId: tempWashInventoryFiltersFromQueryStrings.accountId,
      locationNumber: tempWashInventoryFiltersFromQueryStrings.locationNumber,
      userId: tempWashInventoryFiltersFromQueryStrings.userId,
      washTypeId: tempWashInventoryFiltersFromQueryStrings.washTypeId,
      vehicleNumber: tempWashInventoryFiltersFromQueryStrings.vehicleNumber,
    }).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    Object.values(tempFilterValuesFromQueryStrings).length > 0 &&
      setFilterValues({
        ...filterValues,
        ...tempFilterValuesFromQueryStrings,
      });
    setWashInventoryFilters({
      ...washInventoryFilters,
      ...tempWashInventoryFiltersFromQueryStrings,
    });
    setWashInventoryPaginationModel({
      ...washInventoryPaginationModel,
      page: tempWashInventoryFiltersFromQueryStrings.page || 0,
      pageSize: tempWashInventoryFiltersFromQueryStrings.pageSize || 10,
    });
  }, []);

  useEffect(() => {
    const filters = Object.entries(washInventoryFilters).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    setQueryParams({
      ...filters,
    });
  }, [washInventoryFilters]);

  return (
    <Box
      sx={{
        marginTop: "50px",
        width: "100%",
        boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "10px",
      }}
    >
      <Toast setOptions={setToastOptions} options={toastOptions} />
      <Box>
        <Grid2
          container
          direction={"column"}
          spacing={2}
          sx={{
            backgroundColor: theme.palette.secondary.light,
            padding: "20px",
            paddingTop: "0px",
            paddingBottom: "0px",
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            borderBottom: "1px solid #E5E5E5",
          }}
        >
          <Tabs
            tabItems={tabItems}
            onTabChange={handleTabChange}
            value={selectedTab}
          />
        </Grid2>
        <Grid2
          container
          direction={"column"}
          spacing={2}
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <WashInventoryToolBar
            onBulkEdit={handleBulkUpdateButtonClick}
            onRefresh={handleRefresh}
            onDownload={handleDownload}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            resetPage={resetPage}
            disableBulkEditBtn={
              selectedWashInventories.length > 1 ? false : true
            }
            isWashTabSelected={isWashTabSelected()}
          />
        </Grid2>
        <Grid2 container direction={"column"} spacing={2}>
          <DataGrid
            columns={columns}
            rows={
              isWashTabSelected()
                ? washInventoryList?.items || []
                : notesList?.items || []
            }
            loading={
              fetchWashInventoryQuery.isLoading ||
              fetchWashInventoryQuery.isRefetching ||
              fetchWashNotesQuery.isLoading ||
              fetchWashNotesQuery.isRefetching ||
              downloadWashInventoryQuery.isLoading ||
              downloadWashInventoryQuery.isRefetching ||
              updateWashInventoryMutation.isLoading ||
              updateWashNoteMutation.isLoading ||
              deleteWashNoteMutation.isLoading ||
              deleteWashInventoryMutation.isLoading ||
              deleteWashInventoriesMutation.isLoading ||
              createWashInventoryMutation.isLoading ||
              bulkUpdateWashInventoryMutation.isLoading
            }
            sortModel={[
              {
                field: washInventoryFilters.sortBy || "",
                sort:
                  (washInventoryFilters.sortOrder as GridSortDirection) || null,
              },
            ]}
            onSortModelChange={handleSortModelChange}
            sortingMode="server"
            paginationModel={washInventoryPaginationModel}
            setPaginationModel={setWashInventoryPaginationModel}
            onSelectedRowIdsChange={handleRowSelectionChange}
            totalRecords={
              isWashTabSelected()
                ? washInventoryList?.totalItems
                : notesList?.totalItems
            }
            paginationMode="server"
            rowCount={
              isWashTabSelected()
                ? washInventoryList?.totalItems
                  ? washInventoryList?.totalItems
                  : 0
                : notesList?.totalItems
                  ? notesList?.totalItems
                  : 0
            }
            pageSize={washInventoryList?.pageSize}
            sx={{
              border: "none",
              backgroundColor: theme.palette.secondary.light,
              padding: "20px",
              paddingTop: "0px",
              "& .MuiSvgIcon-root": {
                fontSize: 16,
              },
              borderBottomLeftRadius: "10px",
              borderBottomRightRadius: "10px",
              height: "66vh",
              "& .MuiDataGrid-columnHeader:not(:last-child)": {
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: "30%",
                  width: "2px",
                  backgroundColor: "rgba(224, 224, 224, 1)",
                  opacity: 0,
                },
              },
              "& .MuiDataGrid-columnHeader:not(:last-child):hover": {
                "&::after": {
                  opacity: 1,
                },
              },
            }}
            footerProps={{
              addBtnLabel:
                hasWashInventoryWritePermission && isWashTabSelected()
                  ? "Add Wash"
                  : "",
              hasAddIcon: true,
              hasRemoveIcon: true,
              removeBtnLabel:
                hasWashInventoryDeletePermission && isWashTabSelected()
                  ? "Delete"
                  : "",
              onAddButtonClick: handleAddButtonClick,
              handleConfirmRemove: handleRemoveButtonClick,
              disableRemoveBtn:
                selectedWashInventories.length > 0 ? false : true,
              confirmationModalTitle: "Delete Washes",
              confirmationModalMessage:
                selectedWashInventories.length == 1
                  ? "Are you sure you want to delete this wash?"
                  : `Are you sure you want to delete these ${selectedWashInventories.length} washes?`,
            }}
          />
        </Grid2>
        <Modal
          open={isBulkEditModalOpen}
          onClose={handleCloseBulkEditModal}
          title="Bulk Edit"
          content={
            <BulkEditWashInventoryForm
              id={"bulkEditForm"}
              handleEdit={handleBulkEdit}
              defaultValues={undefined}
              isBulkEdit={true}
            />
          }
          form={"bulkEditForm"}
          primaryButtonText="Confirm"
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={handleCloseBulkEditModal}
          sx={{
            modal: {
              width: { lg: "30%", xs: "80%", sm: "50%" },
            },
          }}
        />
      </Box>
      <Modal
        open={isAddWashModalOpen}
        onClose={handleCloseWashModal}
        title={"Add Wash"}
        content={
          <ManageWashInventoryForm
            id={"addWashInventoryForm"}
            handleEdit={handleCreateWash}
            isCreateMode={true}
          ></ManageWashInventoryForm>
        }
        form={"addWashInventoryForm"}
        primaryButtonText={"Save"}
        secondaryButtonText={"Cancel"}
        onSecondaryButtonClick={handleCloseWashModal}
        sx={{
          modal: {
            width: { lg: "30%", xs: "80%", sm: "50%" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            overflowY: "auto",
            flex: 1,
          },
        }}
      />
    </Box>
  );
};
